import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { LeadCaptureSettings } from '../components/LeadCaptureSettings';
import { MailchimpSettings } from '../components/MailchimpSettings';
import { 
  Building2, User, Bell, Mail, FileText, Globe, Shield, 
  Palette, Save, RefreshCw, Key, CreditCard, Clock, Database, Download, HardDrive
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Company Settings
  const [companySettings, setCompanySettings] = useState({
    company_name: 'IEMS Enterprise',
    company_email: '',
    company_phone: '',
    company_address: '',
    vat_number: '',
    registration_number: '',
    website: '',
    logo_url: ''
  });

  // Invoice Settings
  const [invoiceSettings, setInvoiceSettings] = useState({
    invoice_prefix: 'INV',
    quote_prefix: 'QT',
    default_payment_terms: 30,
    default_vat_rate: 15,
    currency: 'ZAR',
    currency_symbol: 'R',
    invoice_notes: 'Thank you for your business!',
    bank_details: ''
  });

  // Notification Settings
  const [notificationSettings, setNotificationSettings] = useState({
    email_notifications: true,
    invoice_reminders: true,
    task_reminders: true,
    lead_notifications: true,
    reminder_days_before: 2
  });

  // Reminder Schedule (Super Admin)
  const [reminderSettings, setReminderSettings] = useState({
    enabled: true,
    interval_minutes: 60,
    window_start: '08:30',
    window_end: '17:00'
  });
  const [sendingDigest, setSendingDigest] = useState(false);
  const [cleaningTasks, setCleaningTasks] = useState(false);

  // User Profile
  const [userProfile, setUserProfile] = useState({
    name: '',
    email: '',
    role: ''
  });

  // Password Change
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Export Preview
  const [exportPreview, setExportPreview] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const [settingsRes, userRes, reminderRes] = await Promise.allSettled([
        axios.get(`${API}/api/settings`),
        axios.get(`${API}/api/auth/me`),
        axios.get(`${API}/api/settings/reminders`)
      ]);

      if (settingsRes.status === 'fulfilled' && settingsRes.value.data) {
        const settings = settingsRes.value.data;
        if (settings.company) setCompanySettings(prev => ({ ...prev, ...settings.company }));
        if (settings.invoice) setInvoiceSettings(prev => ({ ...prev, ...settings.invoice }));
        if (settings.notifications) setNotificationSettings(prev => ({ ...prev, ...settings.notifications }));
      }

      if (reminderRes.status === 'fulfilled' && reminderRes.value.data) {
        const { enabled, interval_minutes, window_start, window_end } = reminderRes.value.data;
        setReminderSettings({ enabled, interval_minutes, window_start, window_end });
      }

      if (userRes.status === 'fulfilled' && userRes.value.data) {
        setUserProfile(userRes.value.data);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveCompanySettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/settings/company`, companySettings);
      toast.success('Company settings saved');
    } catch (error) {
      toast.error('Failed to save company settings');
    } finally {
      setSaving(false);
    }
  };

  const saveInvoiceSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/settings/invoice`, invoiceSettings);
      toast.success('Invoice settings saved');
    } catch (error) {
      toast.error('Failed to save invoice settings');
    } finally {
      setSaving(false);
    }
  };

  const saveNotificationSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/settings/notifications`, notificationSettings);
      toast.success('Notification settings saved');
    } catch (error) {
      toast.error('Failed to save notification settings');
    } finally {
      setSaving(false);
    }
  };

  const saveReminderSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/settings/reminders`, reminderSettings);
      toast.success('Reminder schedule saved');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save reminder schedule');
    } finally {
      setSaving(false);
    }
  };

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const res = await axios.post(`${API}/api/notifications/run-digest`);
      toast.success(`Digest sent to ${res.data.users_emailed} user(s)`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to send digest');
    } finally {
      setSendingDigest(false);
    }
  };

  const changePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/api/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success('Password changed successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const loadExportPreview = async () => {
    try {
      const res = await axios.get(`${API}/api/export-database/preview`);
      setExportPreview(res.data);
    } catch (error) {
      console.error('Failed to load export preview:', error);
    }
  };

  const exportDatabase = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/api/export-database`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `iems_database_export_${today}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Database exported successfully!');
    } catch (error) {
      toast.error('Failed to export database');
      console.error('Export error:', error);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-muted-foreground">Manage your system preferences and configurations</p>
      </div>

      <Tabs defaultValue="company" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4 sm:grid-cols-7 lg:w-auto lg:inline-grid h-auto">
          <TabsTrigger value="company" className="gap-2">
            <Building2 className="h-4 w-4" /> Company
          </TabsTrigger>
          <TabsTrigger value="invoice" className="gap-2">
            <FileText className="h-4 w-4" /> Invoice
          </TabsTrigger>
          <TabsTrigger value="notifications" className="gap-2">
            <Bell className="h-4 w-4" /> Notifications
          </TabsTrigger>
          <TabsTrigger value="profile" className="gap-2">
            <User className="h-4 w-4" /> Profile
          </TabsTrigger>
          <TabsTrigger value="security" className="gap-2">
            <Shield className="h-4 w-4" /> Security
          </TabsTrigger>
          <TabsTrigger value="data" className="gap-2" onClick={loadExportPreview}>
            <Database className="h-4 w-4" /> Data
          </TabsTrigger>
          <TabsTrigger value="integrations" className="gap-2" data-testid="settings-integrations-tab">
            <Globe className="h-4 w-4" /> Integrations
          </TabsTrigger>
        </TabsList>

        {/* Company Settings */}
        <TabsContent value="company">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" /> Company Information
              </CardTitle>
              <CardDescription>
                Configure your company details that appear on invoices and documents
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input
                    value={companySettings.company_name}
                    onChange={(e) => setCompanySettings({ ...companySettings, company_name: e.target.value })}
                    placeholder="Your Company Name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={companySettings.company_email}
                    onChange={(e) => setCompanySettings({ ...companySettings, company_email: e.target.value })}
                    placeholder="info@company.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={companySettings.company_phone}
                    onChange={(e) => setCompanySettings({ ...companySettings, company_phone: e.target.value })}
                    placeholder="+27 11 123 4567"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Website</Label>
                  <Input
                    value={companySettings.website}
                    onChange={(e) => setCompanySettings({ ...companySettings, website: e.target.value })}
                    placeholder="https://www.company.com"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Address</Label>
                <Textarea
                  value={companySettings.company_address}
                  onChange={(e) => setCompanySettings({ ...companySettings, company_address: e.target.value })}
                  placeholder="123 Main Street, Johannesburg, 2000"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>VAT Number</Label>
                  <Input
                    value={companySettings.vat_number}
                    onChange={(e) => setCompanySettings({ ...companySettings, vat_number: e.target.value })}
                    placeholder="VAT123456789"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Registration Number</Label>
                  <Input
                    value={companySettings.registration_number}
                    onChange={(e) => setCompanySettings({ ...companySettings, registration_number: e.target.value })}
                    placeholder="2024/123456/07"
                  />
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={saveCompanySettings} disabled={saving}>
                  <Save className="h-4 w-4 mr-2" /> Save Company Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoice Settings */}
        <TabsContent value="invoice">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" /> Invoice & Quotation Settings
              </CardTitle>
              <CardDescription>
                Configure default settings for invoices and quotations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Invoice Prefix</Label>
                  <Input
                    value={invoiceSettings.invoice_prefix}
                    onChange={(e) => setInvoiceSettings({ ...invoiceSettings, invoice_prefix: e.target.value })}
                    placeholder="INV"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Quote Prefix</Label>
                  <Input
                    value={invoiceSettings.quote_prefix}
                    onChange={(e) => setInvoiceSettings({ ...invoiceSettings, quote_prefix: e.target.value })}
                    placeholder="QT"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Default Payment Terms (Days)</Label>
                  <Select 
                    value={String(invoiceSettings.default_payment_terms)} 
                    onValueChange={(v) => setInvoiceSettings({ ...invoiceSettings, default_payment_terms: parseInt(v) })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 Days</SelectItem>
                      <SelectItem value="14">14 Days</SelectItem>
                      <SelectItem value="30">30 Days</SelectItem>
                      <SelectItem value="45">45 Days</SelectItem>
                      <SelectItem value="60">60 Days</SelectItem>
                      <SelectItem value="90">90 Days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select 
                    value={invoiceSettings.currency} 
                    onValueChange={(v) => setInvoiceSettings({ ...invoiceSettings, currency: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ZAR">ZAR - South African Rand</SelectItem>
                      <SelectItem value="USD">USD - US Dollar</SelectItem>
                      <SelectItem value="EUR">EUR - Euro</SelectItem>
                      <SelectItem value="GBP">GBP - British Pound</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Currency Symbol</Label>
                  <Input
                    value={invoiceSettings.currency_symbol}
                    onChange={(e) => setInvoiceSettings({ ...invoiceSettings, currency_symbol: e.target.value })}
                    placeholder="R"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Default VAT Rate (%)</Label>
                  <Input
                    type="number"
                    value={invoiceSettings.default_vat_rate}
                    onChange={(e) => setInvoiceSettings({ ...invoiceSettings, default_vat_rate: parseFloat(e.target.value) || 0 })}
                    placeholder="15"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Default Invoice Notes</Label>
                <Textarea
                  value={invoiceSettings.invoice_notes}
                  onChange={(e) => setInvoiceSettings({ ...invoiceSettings, invoice_notes: e.target.value })}
                  placeholder="Thank you for your business!"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Bank Details (for invoices)</Label>
                <Textarea
                  value={invoiceSettings.bank_details}
                  onChange={(e) => setInvoiceSettings({ ...invoiceSettings, bank_details: e.target.value })}
                  placeholder="Bank: Standard Bank&#10;Account: 123456789&#10;Branch: 051001"
                  rows={3}
                />
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={saveInvoiceSettings} disabled={saving}>
                  <Save className="h-4 w-4 mr-2" /> Save Invoice Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notification Settings */}
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" /> Notification Preferences
              </CardTitle>
              <CardDescription>
                Configure how and when you receive notifications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Email Notifications</Label>
                  <p className="text-sm text-muted-foreground">Receive notifications via email</p>
                </div>
                <Switch
                  checked={notificationSettings.email_notifications}
                  onCheckedChange={(checked) => setNotificationSettings({ ...notificationSettings, email_notifications: checked })}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Invoice Payment Reminders</Label>
                  <p className="text-sm text-muted-foreground">Get notified about overdue invoices</p>
                </div>
                <Switch
                  checked={notificationSettings.invoice_reminders}
                  onCheckedChange={(checked) => setNotificationSettings({ ...notificationSettings, invoice_reminders: checked })}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Task Due Reminders</Label>
                  <p className="text-sm text-muted-foreground">Get notified before tasks are due</p>
                </div>
                <Switch
                  checked={notificationSettings.task_reminders}
                  onCheckedChange={(checked) => setNotificationSettings({ ...notificationSettings, task_reminders: checked })}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>New Lead Notifications</Label>
                  <p className="text-sm text-muted-foreground">Get notified when new leads are created</p>
                </div>
                <Switch
                  checked={notificationSettings.lead_notifications}
                  onCheckedChange={(checked) => setNotificationSettings({ ...notificationSettings, lead_notifications: checked })}
                />
              </div>

              <div className="space-y-2 pt-4 border-t">
                <Label>Reminder Days Before Due Date</Label>
                <Select 
                  value={String(notificationSettings.reminder_days_before)} 
                  onValueChange={(v) => setNotificationSettings({ ...notificationSettings, reminder_days_before: parseInt(v) })}
                >
                  <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 Day Before</SelectItem>
                    <SelectItem value="2">2 Days Before</SelectItem>
                    <SelectItem value="3">3 Days Before</SelectItem>
                    <SelectItem value="5">5 Days Before</SelectItem>
                    <SelectItem value="7">7 Days Before</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={saveNotificationSettings} disabled={saving}>
                  <Save className="h-4 w-4 mr-2" /> Save Notification Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Reminder Schedule (Super Admin) */}
          <Card className="mt-4" data-testid="reminder-schedule-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" /> Email Reminder Schedule
              </CardTitle>
              <CardDescription>
                Controls how often the system emails every user their outstanding tasks, projects and leads. Super Admin only.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Reminders Enabled</Label>
                  <p className="text-sm text-muted-foreground">Turn the automatic reminder emails on or off</p>
                </div>
                <Switch
                  checked={reminderSettings.enabled}
                  onCheckedChange={(checked) => setReminderSettings({ ...reminderSettings, enabled: checked })}
                  data-testid="reminder-enabled-switch"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Reminder Interval</Label>
                  <Select
                    value={String(reminderSettings.interval_minutes)}
                    onValueChange={(v) => setReminderSettings({ ...reminderSettings, interval_minutes: parseInt(v) })}
                  >
                    <SelectTrigger data-testid="reminder-interval-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">Every 30 minutes</SelectItem>
                      <SelectItem value="60">Every 1 hour</SelectItem>
                      <SelectItem value="120">Every 2 hours</SelectItem>
                      <SelectItem value="240">Every 4 hours</SelectItem>
                      <SelectItem value="1440">Once daily</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Window Start (SA time)</Label>
                  <Input
                    type="time"
                    value={reminderSettings.window_start}
                    onChange={(e) => setReminderSettings({ ...reminderSettings, window_start: e.target.value })}
                    data-testid="reminder-window-start"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Window End (SA time)</Label>
                  <Input
                    type="time"
                    value={reminderSettings.window_end}
                    onChange={(e) => setReminderSettings({ ...reminderSettings, window_end: e.target.value })}
                    data-testid="reminder-window-end"
                  />
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Reminders only go out between the start and end times, Monday to Sunday. Each user receives their own tasks, projects and leads; admins also receive a team-wide summary.
              </p>

              <div className="flex justify-between pt-4 border-t">
                <Button variant="outline" onClick={sendDigestNow} disabled={sendingDigest} data-testid="send-digest-now-btn">
                  <Mail className="h-4 w-4 mr-2" /> {sendingDigest ? 'Sending...' : 'Send Reminder Now'}
                </Button>
                <Button onClick={saveReminderSettings} disabled={saving} data-testid="save-reminder-settings-btn">
                  <Save className="h-4 w-4 mr-2" /> Save Reminder Schedule
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Profile Settings */}
        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" /> User Profile
              </CardTitle>
              <CardDescription>
                View and manage your account information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="h-16 w-16 rounded-full bg-slate-200 flex items-center justify-center">
                  <User className="h-8 w-8 text-slate-500" />
                </div>
                <div>
                  <p className="font-semibold text-lg">{userProfile.name || 'User'}</p>
                  <p className="text-muted-foreground">{userProfile.email}</p>
                  <Badge variant="outline" className="mt-1 capitalize">{userProfile.role || 'User'}</Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input
                    value={userProfile.name || ''}
                    onChange={(e) => setUserProfile({ ...userProfile, name: e.target.value })}
                    placeholder="Your name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    value={userProfile.email || ''}
                    disabled
                    className="bg-slate-50"
                  />
                  <p className="text-xs text-muted-foreground">Email cannot be changed</p>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button disabled={saving}>
                  <Save className="h-4 w-4 mr-2" /> Update Profile
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Settings */}
        <TabsContent value="security">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" /> Change Password
              </CardTitle>
              <CardDescription>
                Update your account password for security
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="max-w-md space-y-4">
                <div className="space-y-2">
                  <Label>Current Password</Label>
                  <Input
                    type="password"
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                    placeholder="Enter current password"
                  />
                </div>
                <div className="space-y-2">
                  <Label>New Password</Label>
                  <Input
                    type="password"
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                    placeholder="Enter new password"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Confirm New Password</Label>
                  <Input
                    type="password"
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                    placeholder="Confirm new password"
                  />
                </div>

                <div className="flex justify-end pt-4">
                  <Button onClick={changePassword} disabled={saving || !passwordForm.current_password || !passwordForm.new_password}>
                    <Key className="h-4 w-4 mr-2" /> Change Password
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Data Export Settings */}
        <TabsContent value="data">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" /> Database Export
              </CardTitle>
              <CardDescription>
                Export all your system data for backup or migration to another system
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <HardDrive className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-900">Local MongoDB Database</p>
                    <p className="text-sm text-blue-700 mt-1">
                      Your data is stored locally in MongoDB. The export will include all collections 
                      in JSON format, packaged as a ZIP file for easy download.
                    </p>
                  </div>
                </div>
              </div>

              {exportPreview && (
                <div className="space-y-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <FileText className="h-4 w-4" /> Data Preview
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {Object.entries(exportPreview.collections || {}).map(([name, count]) => (
                      <div key={name} className="p-3 bg-slate-50 rounded-lg border">
                        <p className="text-sm font-medium capitalize">{name.replace(/_/g, ' ')}</p>
                        <p className="text-2xl font-bold text-slate-700">{count}</p>
                        <p className="text-xs text-muted-foreground">records</p>
                      </div>
                    ))}
                  </div>
                  <div className="pt-2 border-t">
                    <p className="text-sm text-muted-foreground">
                      Total: <span className="font-semibold text-foreground">{exportPreview.total_documents}</span> documents across all collections
                    </p>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button 
                  onClick={exportDatabase} 
                  disabled={exporting}
                  className="gap-2"
                  data-testid="export-database-btn"
                >
                  {exporting ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  {exporting ? 'Exporting...' : 'Export All Data (ZIP)'}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={loadExportPreview}
                  className="gap-2"
                >
                  <RefreshCw className="h-4 w-4" /> Refresh Preview
                </Button>
              </div>

              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> The export contains all your business data including users, leads, 
                  invoices, clients, projects, and settings. Passwords are hashed and secure. 
                  Keep this file safe as it contains sensitive business information.
                </p>
              </div>

              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg space-y-3" data-testid="task-cleanup-section">
                <div>
                  <p className="font-medium text-red-900">Task Cleanup (one-time)</p>
                  <p className="text-sm text-red-700 mt-1">
                    Deletes the CSV-imported task batch (17 March 2026) and known test tasks, then marks every
                    remaining task NOT due in the current week as Done. Super Admin only — cannot be undone.
                  </p>
                </div>
                <Button
                  variant="destructive"
                  disabled={cleaningTasks}
                  onClick={async () => {
                    if (!window.confirm('Delete CSV-imported + test tasks and mark all tasks not due this week as Done? This cannot be undone.')) return;
                    setCleaningTasks(true);
                    try {
                      const res = await axios.post(`${API}/api/tasks/cleanup-imported`);
                      const d = res.data;
                      toast.success(`Cleanup done: ${d.imported_deleted} imported + ${d.test_deleted} test tasks deleted, ${d.marked_done} marked done, ${d.remaining} tasks remain`);
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'Cleanup failed');
                    } finally {
                      setCleaningTasks(false);
                    }
                  }}
                  data-testid="task-cleanup-btn"
                >
                  {cleaningTasks ? 'Cleaning...' : 'Run Task Cleanup'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations */}
        <TabsContent value="integrations">
          <div className="space-y-4">
            <LeadCaptureSettings />
            <MailchimpSettings />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
